import { Fragment, type ReactNode } from "react";
import { inline } from "../chats/RichText.tsx";

// The Markdown of shared documents (docs/dokumente-konzept.md §4, text-core Markdown.kt):
// #/##/### headings, lists with one level (2 spaces), - [ ] / - [x] checklists, > quotes, ---,
// inline bold, italic, strike, code and links. Rendered as React nodes, never as HTML.

const LIST = /^(\s*)([-*+]|(\d{1,9})[.)])\s+(\[([ xX])\]\s*)?(.*)$/;
const BOX = /^(\s*(?:[-*+]|\d{1,9}[.)])\s+\[)([ xX])(].*)$/;

/** `[ ]` ↔ `[x]` on [line] (Markdown.toggleCheckbox); other lines unchanged. */
export function toggleCheckbox(text: string, line: number): string {
  const lines = text.split("\n");
  const current = lines[line];
  const m = current === undefined ? null : BOX.exec(current);
  if (!m) return text;
  lines[line] = m[1] + (m[2] === " " ? "x" : " ") + m[3];
  return lines.join("\n");
}

export function MarkdownView({ text, onToggle }: { text: string; onToggle?: (line: number) => void }) {
  const out: ReactNode[] = [];
  const lines = text.split("\n");
  let list: { items: ReactNode[]; ordered: boolean } | null = null;
  const flush = () => {
    if (!list) return;
    out.push(list.ordered ? <ol key={out.length}>{list.items}</ol> : <ul key={out.length}>{list.items}</ul>);
    list = null;
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const m = LIST.exec(line);
    if (m) {
      const ordered = Boolean(m[3]);
      if (!list || list.ordered !== ordered) {
        flush();
        list = { items: [], ordered };
      }
      const indent = m[1]!.replace(/\t/g, "  ").length >= 2;
      const box = m[5];
      list.items.push(
        <li key={i} className={[indent ? "md-indent" : "", box !== undefined ? "md-task" : ""].join(" ")}>
          {box !== undefined && <input type="checkbox" checked={box !== " "} disabled={!onToggle} onChange={() => onToggle?.(i)} aria-label="Erledigt" />}
          <span className={box && box !== " " ? "md-done" : undefined}>{inline(m[6] ?? "", `l${i}`)}</span>
        </li>,
      );
      return;
    }
    flush();
    if (!line.trim()) return;
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const content = inline(h[2]!, `h${i}`);
      out.push(h[1]!.length === 1 ? <h1 key={i}>{content}</h1> : h[1]!.length === 2 ? <h2 key={i}>{content}</h2> : <h3 key={i}>{content}</h3>);
    } else if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      out.push(<hr key={i} />);
    } else if (line.startsWith(">")) {
      out.push(<blockquote key={i}>{inline(line.replace(/^>\s?/, ""), `q${i}`)}</blockquote>);
    } else {
      out.push(<p key={i}>{inline(line, `p${i}`)}</p>);
    }
  });
  flush();
  return <Fragment>{out}</Fragment>;
}
